import React from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { Col, Container, Row } from 'react-bootstrap';

import AppLayout from '../../../layouts/AppLayout/AppLayout';
import { useLanguage } from '../../../context/LanguageProvider/LanguageProvider';
import { EXPERTISE_ITEMS, getExpertiseBySlug } from '../../../data/expertises';
import { NioButton, NioIcon } from '../../../components';
import ExpertiseImage from '../../../components/ExpertiseImage/ExpertiseImage';
import ExpertiseTechTags from '../../../components/ExpertiseTechTags/ExpertiseTechTags';

function ExpertiseDetail() {
  const { slug } = useParams();
  const { t } = useLanguage();
  const item = getExpertiseBySlug(slug);

  if (!item) {
    return <Navigate to="/404" replace />;
  }

  const otherItems = EXPERTISE_ITEMS.filter((entry) => entry.slug !== item.slug);

  return (
    <AppLayout variant={2} titleKey={item.titleKey} rootClass="layout-1">
      <div className="jassp-expertise-detail-page">
        <section className="jassp-expertises-hero pt-7 pt-lg-120 pb-5 pb-lg-6">
          <Container>
            <p className="mb-4">
              <Link to="/expertises" className="jassp-expertise-detail-back link-light text-decoration-none">
                <NioIcon name="arrow-left" className="me-2" />
                {t('expertises.detail.backToAll')}
              </Link>
            </p>
            <Row className="justify-content-center">
              <Col lg={10} xl={9}>
                <div className="d-flex align-items-start gap-3 mb-3">
                  <span className="jassp-expertise-detail-hero-icon" aria-hidden="true">
                    <NioIcon name={item.icon} />
                  </span>
                  <h1 className="jassp-expertises-hero-title display-5 fw-bold mb-0">{t(item.titleKey)}</h1>
                </div>
                <p className="jassp-expertises-hero-lead fs-18 mb-4">{t(item.homeBodyKey)}</p>
                <ExpertiseTechTags keywordKeys={item.techKeywordsKeys} />
              </Col>
            </Row>
          </Container>
        </section>

        <section className="py-6 py-lg-7">
          <Container>
            <Row className="align-items-start gy-5">
              <Col lg={5} className="text-center text-lg-start">
                <div className="jassp-expertise-detail-photo-wrap jassp-expertise-detail-photo-wrap--inline">
                  <ExpertiseImage item={item} alt={t(item.altKey)} variant="inline" />
                </div>
              </Col>
              <Col lg={7}>
                <div className="jassp-expertise-detail-content">
                  <p className="fs-18 text-gray-800 mb-4">{t(item.bodyKey)}</p>
                  <ExpertiseTechTags keywordKeys={item.techKeywordsKeys} className="mb-4" />
                  {item.highlightsKeys?.length > 0 && (
                    <>
                      <h2 className="h5 fw-bold mb-3">{t('expertises.detail.highlightsTitle')}</h2>
                      <ul className="jassp-expertise-detail-highlights ps-3 mb-5">
                        {item.highlightsKeys.map((key) => (
                          <li key={key} className="fs-16 text-gray-700 mb-2">
                            {t(key)}
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                  <NioButton as="link" to="/contact-us" label={t('expertises.methodology.cta')} className="btn-primary">
                    <NioIcon name="arrow-right" className="ms-2" />
                  </NioButton>
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        <section className="jassp-expertise-block--alt py-6 py-lg-7">
          <Container>
            <h2 className="h4 fw-bold mb-4">{t('expertises.detail.otherTitle')}</h2>
            <div className="jassp-expertises-icon-grid jassp-expertises-icon-grid--light">
              {otherItems.map((entry) => (
                <Link key={entry.slug} to={`/expertises/${entry.slug}`} className="jassp-expertises-icon-card">
                  <span className="jassp-expertises-icon-wrap" aria-hidden="true">
                    <NioIcon name={entry.icon} />
                  </span>
                  <span className="jassp-expertises-icon-label">{t(entry.titleKey)}</span>
                </Link>
              ))}
            </div>
          </Container>
        </section>
      </div>
    </AppLayout>
  );
}

export default ExpertiseDetail;
